import { Chip, type ChipProps } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import type {
  TenantStatus,
  TenantSignupMode,
  TenantUserStatus,
  HostnameType,
  InvitationStatus,
} from '@/api/types';

type ChipColor = ChipProps['color'];

interface Variant {
  color: ChipColor;
  i18nKey: string;
  fallback: string;
}

const TENANT_STATUSES: Record<TenantStatus, Variant> = {
  ACTIVE: { color: 'success', i18nKey: 'tenants.status.ACTIVE', fallback: 'Active' },
  INACTIVE: { color: 'default', i18nKey: 'tenants.status.INACTIVE', fallback: 'Inactive' },
  SUSPENDED: { color: 'error', i18nKey: 'tenants.status.SUSPENDED', fallback: 'Suspended' },
};

const TENANT_SIGNUP_MODES: Record<TenantSignupMode, Variant> = {
  PUBLIC: { color: 'info', i18nKey: 'tenants.signupMode.PUBLIC', fallback: 'Public signup' },
  INVITE_ONLY: {
    color: 'default',
    i18nKey: 'tenants.signupMode.INVITE_ONLY',
    fallback: 'Invite only',
  },
  ADMIN_CREATED_ONLY: {
    color: 'default',
    i18nKey: 'tenants.signupMode.ADMIN_CREATED_ONLY',
    fallback: 'Admin-created only',
  },
  DISABLED: {
    color: 'default',
    i18nKey: 'tenants.signupMode.DISABLED',
    fallback: 'Signup disabled',
  },
};

const MEMBER_STATUSES: Record<TenantUserStatus, Variant> = {
  ACTIVE: { color: 'success', i18nKey: 'members.statuses.ACTIVE', fallback: 'Active' },
  INVITED: { color: 'warning', i18nKey: 'members.statuses.INVITED', fallback: 'Invited' },
  DISABLED: { color: 'default', i18nKey: 'members.statuses.DISABLED', fallback: 'Disabled' },
};

const INVITATION_STATUSES: Record<InvitationStatus, Variant> = {
  PENDING: { color: 'warning', i18nKey: 'invitations.statuses.PENDING', fallback: 'Pending' },
  ACCEPTED: { color: 'success', i18nKey: 'invitations.statuses.ACCEPTED', fallback: 'Accepted' },
  REVOKED: { color: 'error', i18nKey: 'invitations.statuses.REVOKED', fallback: 'Revoked' },
  EXPIRED: { color: 'default', i18nKey: 'invitations.statuses.EXPIRED', fallback: 'Expired' },
};

const HOSTNAME_TYPES: Record<HostnameType, Variant> = {
  APP: { color: 'primary', i18nKey: 'hostnames.types.APP', fallback: 'App' },
  LOGIN: { color: 'info', i18nKey: 'hostnames.types.LOGIN', fallback: 'Login' },
  AUTH: { color: 'default', i18nKey: 'hostnames.types.AUTH', fallback: 'Auth' },
  API: { color: 'default', i18nKey: 'hostnames.types.API', fallback: 'API' },
  ASSET: { color: 'default', i18nKey: 'hostnames.types.ASSET', fallback: 'Asset' },
  BRAND: { color: 'secondary', i18nKey: 'hostnames.types.BRAND', fallback: 'Brand' },
};

export interface StatusChipProps {
  kind: 'tenantStatus' | 'tenantSignupMode' | 'memberStatus' | 'hostnameType' | 'invitationStatus';
  value: string;
  size?: ChipProps['size'];
}

export function StatusChip({ kind, value, size = 'small' }: StatusChipProps) {
  const { t } = useCopy();
  const variant = pickVariant(kind, value);
  if (!variant) return <Chip label={value} size={size} />;
  const chipColor = variant.color ?? 'default';
  return <Chip label={t(variant.i18nKey, variant.fallback)} color={chipColor} size={size} />;
}

function pickVariant(kind: StatusChipProps['kind'], value: string): Variant | null {
  switch (kind) {
    case 'tenantStatus':
      return TENANT_STATUSES[value as TenantStatus] ?? null;
    case 'tenantSignupMode':
      return TENANT_SIGNUP_MODES[value as TenantSignupMode] ?? null;
    case 'memberStatus':
      return MEMBER_STATUSES[value as TenantUserStatus] ?? null;
    case 'hostnameType':
      return HOSTNAME_TYPES[value as HostnameType] ?? null;
    case 'invitationStatus':
      return INVITATION_STATUSES[value as InvitationStatus] ?? null;
    default:
      return null;
  }
}
